import React from "react";
import PropTypes from "prop-types";
import {
  Avatar,
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import MDTypography from "components/MDTypography";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import SmartToyOutlinedIcon from "@mui/icons-material/SmartToyOutlined";
import PaletteOutlinedIcon from "@mui/icons-material/PaletteOutlined";
import PsychologyOutlinedIcon from "@mui/icons-material/PsychologyOutlined";
import RocketLaunchOutlinedIcon from "@mui/icons-material/RocketLaunchOutlined";

const DetailItem = ({ label, value }) => (
  <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
    <MDTypography variant="body2" fontWeight="medium">
      {label}:
    </MDTypography>
    <MDTypography variant="body2" color="text">
      {value}
    </MDTypography>
  </Box>
);

DetailItem.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.node.isRequired,
};

const ColorDisplay = ({ color }) => (
  <Box
    sx={{
      width: 20,
      height: 20,
      borderRadius: "4px",
      backgroundColor: color,
      border: "1px solid #ccc",
    }}
  />
);

ColorDisplay.propTypes = {
  color: PropTypes.string.isRequired,
};

function ReviewStep({ name, welcomeMessage, headerColor, buttonColor, avatarUrl, dataSources }) {
  const knowledgeType = dataSources[0]?.knowledgeType || "N/A";
  const sourceUrl = dataSources[0]?.url || "N/A";

  return (
    <Box sx={{ textAlign: "center" }}>
      <MDTypography variant="h4" fontWeight="bold">
        You&apos;re All Set!
      </MDTypography>
      <MDTypography variant="body1" color="text" mb={4}>
        Your chatbot is ready to start helping your customers.
      </MDTypography>

      <Grid container spacing={3} sx={{ textAlign: "left" }}>
        <Grid item xs={12} md={6}>
          <Card sx={{ borderLeft: "4px solid", borderColor: "info.main", height: "100%" }}>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <SmartToyOutlinedIcon color="info" sx={{ mr: 1 }} />
                <MDTypography variant="h6" fontWeight="bold">
                  Basic Setup
                </MDTypography>
              </Box>
              <DetailItem label="Name" value={name} />
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <MDTypography variant="body2" fontWeight="medium">
                  Avatar:
                </MDTypography>
                <Avatar src={avatarUrl} sx={{ width: 40, height: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card sx={{ borderLeft: "4px solid", borderColor: "warning.main", height: "100%" }}>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <PaletteOutlinedIcon color="warning" sx={{ mr: 1 }} />
                <MDTypography variant="h6" fontWeight="bold">
                  Appearance
                </MDTypography>
              </Box>
              <DetailItem label="Header Color" value={<ColorDisplay color={headerColor} />} />
              <DetailItem label="Button Color" value={<ColorDisplay color={buttonColor} />} />
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12}>
          <Card sx={{ borderLeft: "4px solid", borderColor: "primary.main" }}>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <PsychologyOutlinedIcon color="primary" sx={{ mr: 1 }} />
                <MDTypography variant="h6" fontWeight="bold">
                  Knowledge & Behavior
                </MDTypography>
              </Box>
              <DetailItem label="Welcome Message" value={welcomeMessage} />
              <DetailItem label="Knowledge Type" value={knowledgeType} />
              <DetailItem label="Source" value={sourceUrl} />
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Box
            sx={{
              backgroundColor: "#e7f3ff",
              borderRadius: 2,
              p: 3,
              mt: 2,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
              <RocketLaunchOutlinedIcon sx={{ mr: 1, color: "#176bd1" }} />
              <MDTypography variant="h6" fontWeight="bold" sx={{ color: "#176bd1" }}>
                What&apos;s Next?
              </MDTypography>
            </Box>
            <List sx={{ p: 0 }}>
              {[
                "Test your chatbot using the preview window",
                "Add more intents and responses in the configuration panel",
                "Deploy your chatbot to your website when you're ready",
              ].map((text, index) => (
                <ListItem key={index} disableGutters>
                  <ListItemIcon sx={{ minWidth: "28px" }}>
                    <Box
                      sx={{
                        width: 6,
                        height: 6,
                        borderRadius: "50%",
                        backgroundColor: "#176bd1",
                      }}
                    />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <MDTypography variant="body2" sx={{ color: "#176bd1" }}>
                        {text}
                      </MDTypography>
                    }
                  />
                </ListItem>
              ))}
            </List>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
}

ReviewStep.propTypes = {
  name: PropTypes.string.isRequired,
  welcomeMessage: PropTypes.string.isRequired,
  headerColor: PropTypes.string.isRequired,
  buttonColor: PropTypes.string.isRequired,
  avatarUrl: PropTypes.string,
  dataSources: PropTypes.array.isRequired,
};

export default ReviewStep;
